import type { EligibilityCheck } from '@compass/client/types/eligibility';
import { Button } from '@components/ui/button';
import { Copy } from 'lucide-react';
import React from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { WorkflowType } from '@/constants/dispositions';
import {
  determineAttemptType,
  getEligibilityCheckStatusDisplay,
} from '@/utils/callsTabs/statusDisplay';
import { copyToClipboard } from '@/utils/clipboard';

import type { PopoverContentProps, TabLabelContext, TabTypeHandler } from './types';

const getLabel = (_data: EligibilityCheck, context: TabLabelContext): string => {
  const { attemptNumber, workflowType, caseState } = context;

  if (
    workflowType === WorkflowType.MEDBV ||
    workflowType === WorkflowType.UNIFIEDMEDBV ||
    workflowType === WorkflowType.BHO ||
    workflowType === WorkflowType.PA_STATUS_PBM ||
    workflowType === WorkflowType.PA_STATUS_MEDBV
  ) {
    return `Attempt ${attemptNumber}`;
  }

  if (workflowType === WorkflowType.MEDICARE_HUMAN) {
    const attemptType = determineAttemptType(caseState);
    return `${attemptType} Attempt ${attemptNumber}`;
  }

  return `Call ${attemptNumber}`;
};

const EligibilityPopoverContent: React.FC<PopoverContentProps<EligibilityCheck>> = ({
  data: eligibilityCheck,
}) => {
  return (
    <>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between gap-8 items-center">
          <span className="text-muted-foreground">EC ID:</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">{eligibilityCheck.id}</span>
            {eligibilityCheck.id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(eligibilityCheck.id!)}
                className="h-6 w-6 p-0"
              >
                <Copy className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Type:</span>
          <span>EligibilityCheck</span>
        </div>
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Status:</span>
          <span>{getEligibilityCheckStatusDisplay(eligibilityCheck.status)}</span>
        </div>
        {eligibilityCheck.payer_id && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Payer ID:</span>
            <span>{eligibilityCheck.payer_id}</span>
          </div>
        )}
        {eligibilityCheck.created_at && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Created:</span>
            <FormattedDateTime value={eligibilityCheck.created_at} />
          </div>
        )}
        {eligibilityCheck.updated_at && (
          <div className="flex justify-between gap-8">
            <span className="text-muted-foreground">Updated:</span>
            <FormattedDateTime value={eligibilityCheck.updated_at} />
          </div>
        )}
      </div>

      {eligibilityCheck.errors && eligibilityCheck.errors.length > 0 && (
        <div className="">
          <div className="bg-secondary p-2 rounded text-xs">
            <div className="text-muted-foreground mb-1">Errors:</div>
            <div className="text-foreground whitespace-pre-wrap max-w-[400px] max-h-20 overflow-auto">
              {eligibilityCheck.errors
                .filter(error => error.description)
                .map(error => error.description)
                .join('\n')}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const eligibilityHandler: TabTypeHandler<EligibilityCheck> = {
  isDisabled: false,
  getLabel,
  getStatusLabel: data => getEligibilityCheckStatusDisplay(data.status),
  PopoverContent: EligibilityPopoverContent,
};
